import * as React from 'react';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

import { TracInviteTypes } from '@services/invites/types/invite-types';

import Flex from '@components/layout/flex';
import MotionOpacity from '@components/motion/motion-opacity';
import InviteArtistInlineCtrl from '../../invites/invite-artist-inline';

import SimpleUser from '@common/types/user/simple-user';
import { RecordLabel } from '@common/types/record-label/record-label';
import UsersSearchCtrl from '../../user/user-search';
import { UserSearchResult } from '@webapp/types/user-search-result';
import { normalizePhoneOrEmail } from '../../../utils/normalizers';
import SmallNotification from '@components/small-notification';

interface OperatorLabelInviteCtrlProps {
  className?: string;
  label: React.ReactNode;
  value?: UserSearchResult;
  currentValues: UserSearchResult[];
  locked?: boolean;
  decoration?: React.ReactNode;
  hasClose?: boolean;
  firstBigger?: boolean;
  noShadow?: boolean;
  customBorderRadius?: string;
  inHalf?: boolean;
  type: TracInviteTypes;
  role: string;
  songName: string;
  filterRepeated?: boolean;
  fromUser?: boolean;
  recordLabel?: RecordLabel;
  onClose?: (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  onChange: (value: string) => void;
  onSelection: (value: UserSearchResult) => void;
}

export const OperatorLabelInviteCtrl: React.FC<
  OperatorLabelInviteCtrlProps
> = ({ filterRepeated = true, ...props }) => {
  const [showInvite, setShowInvite] = React.useState(false);
  const [value, setValue] = React.useState(props.value?.name || '');

  const [showNotification, setShowNotification] = React.useState(false);

  React.useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  React.useEffect(() => {
    setValue(props.value?.name || '');
  }, [props.value]);

  const handleChangeResults = (results: UserSearchResult[]) => {
    const exists = results.find(
      (r) => r.name.toLowerCase() === value.toLocaleLowerCase()
    );
    setShowInvite(!!value && !props.value?.id && !exists);
  };

  const handleSelection = (user: UserSearchResult) => {
    setValue(user.name);
    props.onSelection(user);
  };

  const handleChange = (newValue: string) => {
    setValue(newValue);
    props.onChange(newValue);
  };

  const clear = () => {
    setValue('');
    setShowInvite(false);
  };

  return (
    <MotionOpacity className={props.className}>
      <Flex className={`input-wrapper ${showInvite ? 'mb-10' : ''}`}>
        <UsersSearchCtrl
          className="input-split"
          label={props.label}
          leftDecoration={<GreyCircle />}
          currentValues={props.currentValues
            .filter((i) => i.type === 'user')
            .map((i) => {
              const contact = normalizePhoneOrEmail(i.contact);

              const u: SimpleUser = {
                id: i.id,
                fullName: i.name,
                avatar: i.avatar || '',
                email: contact && contact.type === 'email' ? contact.value : '',
                telephone:
                  contact && contact.type === 'sms' ? contact.value : '',
                username: i.id,
              };

              return u;
            })}
          value={value}
          locked={props.locked}
          decoration={props.decoration}
          hasClose={props.hasClose}
          onClose={props.onClose}
          customBorderRadius={props.customBorderRadius}
          noShadow={props.noShadow}
          parentRelative={true}
          hasTruncate={true}
          onChange={(newValue) => handleChange(newValue)}
          onSelection={(newValue) => handleSelection(newValue)}
          onResultsChange={(results) => handleChangeResults(results)}
        />
        {props.children}
      </Flex>
      {showInvite ? (
        <MotionOpacity>
          <InviteArtistInlineCtrl
            type={props.type}
            name={value}
            className="invite-input"
            role={props.role || ''}
            fromUser={props.fromUser}
            songName={props.songName}
            labelName={props.recordLabel?.name}
            labelId={props.recordLabel?.id}
            onCancel={clear}
            onInvite={() => {
              clear();
              setShowNotification(true);
            }}
          />
        </MotionOpacity>
      ) : null}
      {showNotification ? (
        <SmallNotification
          notificationType="success"
          title={<Translate id="INVITE.LABEL.INVITE.SNACKBAR" />}
        />
      ) : null}
    </MotionOpacity>
  );
};

export default styled(OperatorLabelInviteCtrl)`
  .input-wrapper {
    position: relative;

    & > * {
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 30%;

      &:first-child {
        flex-basis: 70%;
      }
    }
  }

  .invite-input {
    margin-right: 10px;
    width: 100%;
  }
`;

const GreyCircle = styled.div`
  border-radius: 50%;
  width: 24px;
  height: 24px;
  flex-grow: 0;
  background: ${({ theme }) => theme.colors.secondary};
`;
